import { ThemeCard } from "./ThemeCard";

// ThemeSection.tsx
type GapSize = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type ThemeSectionProps = {
  title?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
  // Gap between title/action and children
  gap?: GapSize;
  // Gap between title and action (if both present)
  titleActionGap?: GapSize;
  // Gap between icon and title
  iconTitleGap?: GapSize;
  // Custom gap value (overrides gap prop)
  customGap?: string;
  // Whether to show divider between header and content
  showDivider?: boolean;
  // Padding inside the section
  padding?: 'none' | 'sm' | 'md' | 'lg';
};

const gapClasses: Record<GapSize, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-12'
};

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8'
};

export const ThemeSection: React.FC<ThemeSectionProps> = ({ 
  title, 
  icon, 
  children, 
  className = '',
  action,
  gap = 'md',
  titleActionGap = 'md',
  iconTitleGap = 'sm',
  customGap,
  showDivider = false,
  padding = 'md'
}) => {
  const hasHeader = title || action;
  const gapClass = gapClasses[gap];
  const titleActionGapClass = gapClasses[titleActionGap];
  const iconTitleGapClass = gapClasses[iconTitleGap];
  
  // Use custom gap if provided, otherwise use the gap class
  const contentGapStyle = customGap ? { marginTop: customGap } : {};
  const contentGapClass = !customGap ? gapClass : '';

  return (
    <ThemeCard className={`${paddingClasses[padding]} ${className}`}>
      {hasHeader && (
        <>
          <div className={`flex items-center justify-between ${titleActionGapClass}`}>
            {title && (
              <h2 className={`text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center ${iconTitleGapClass}`}>
                {icon && <span className="text-blue-500">{icon}</span>}
                {title}
              </h2>
            )}
            {action && <div>{action}</div>}
          </div>
          
          {showDivider && (
            <div className="border-t border-gray-200 dark:border-gray-700 my-4" />
          )}
          
          <div className={`mt-5 flex flex-col ${contentGapClass}`} style={contentGapStyle}>
            {children}
          </div>
        </>
      )}
      
      {!hasHeader && (
        <div className={contentGapClass} style={contentGapStyle}>
          {children}
        </div>
      )}
    </ThemeCard>
  );
};

// Optional: Create a more flexible version with compound components
type ThemeSectionCompoundProps = {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
};

const ThemeSectionRoot: React.FC<ThemeSectionCompoundProps> = ({ 
  children, 
  className = '',
  padding = 'md'
}) => {
  return (
    <ThemeCard className={`${paddingClasses[padding]} ${className}`}>
      {children}
    </ThemeCard>
  );
};

type ThemeSectionHeaderProps = {
  title?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  gap?: GapSize;
  iconTitleGap?: GapSize;
  showDivider?: boolean;
  dividerClassName?: string;
};

const ThemeSectionHeader: React.FC<ThemeSectionHeaderProps> = ({
  title,
  icon,
  action,
  gap = 'md',
  iconTitleGap = 'sm',
  showDivider = false,
  dividerClassName = ''
}) => {
  const hasContent = title || action;
  if (!hasContent) return null;
  
  return (
    <>
      <div className={`flex items-center justify-between ${gapClasses[gap]}`}>
        {title && (
          <h2 className={`text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center ${gapClasses[iconTitleGap]}`}>
            {icon && <span className="text-blue-500">{icon}</span>}
            {title}
          </h2>
        )}
        {action && <div>{action}</div>}
      </div>
      {showDivider && (
        <div className={`border-t border-gray-200 dark:border-gray-700 my-4 ${dividerClassName}`} />
      )}
    </>
  );
};

type ThemeSectionContentProps = {
  children: React.ReactNode;
  gap?: GapSize;
  customGap?: string;
  className?: string;
};

const ThemeSectionContent: React.FC<ThemeSectionContentProps> = ({
  children,
  gap = 'md',
  customGap,
  className = ''
}) => {
  const gapClass = gapClasses[gap];
  const gapStyle = customGap ? { marginTop: customGap } : {};
  const finalGapClass = !customGap ? gapClass : '';
  
  return (
    <div className={`${finalGapClass} ${className}`} style={gapStyle}>
      {children}
    </div>
  );
};

// Compound component export
export const ThemeSectionCompound = {
  Root: ThemeSectionRoot,
  Header: ThemeSectionHeader,
  Content: ThemeSectionContent
};